// overview/TreeView — hierarkisk träd-vy med expand/collapse.
// Extraherad ur OverviewDialog (#6).

import { useMemo, useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, ChevronsUpDown, ChevronsDownUp } from 'lucide-react';
import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import { CrossGroupConnectors } from './shared';

export function TreeView({
  blocks,
  connectors,
  groups,
  crossGroupConnectors,
  resolveName,
  resolveColor,
}: {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  crossGroupConnectors: Connector[];
  resolveName: (id: string) => string;
  resolveColor: (id: string) => string;
}) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => new Set(groups.map(g => g.id)));

  const toggleNode = useCallback((id: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedNodes(new Set(groups.map(g => g.id)));
  }, [groups]);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  const topLevelGroups = groups.filter(g => !g.parentGroupId);
  const childGroupsOf = (parentId: string) => groups.filter(g => g.parentGroupId === parentId);
  const allGroupedBlockIds = new Set(groups.flatMap(g => g.blockIds));
  const ungroupedBlocks = blocks.filter(b => !allGroupedBlockIds.has(b.id));

  const groupIds = useMemo(() => new Set(groups.map(g => g.id)), [groups]);
  const blockConnectors = connectors.filter(c => !groupIds.has(c.fromId) && !groupIds.has(c.toId));

  function getGroupBlocks(group: BlockGroup) {
    const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
    const groupBlockIds = new Set(group.blockIds);
    const internal = blockConnectors.filter(c => groupBlockIds.has(c.fromId) && groupBlockIds.has(c.toId));

    // Simple topological sort
    if (groupBlocks.length === 0) return [];
    const adjacency = new Map<string, string[]>();
    const inDegree = new Map<string, number>();
    groupBlocks.forEach(b => { adjacency.set(b.id, []); inDegree.set(b.id, 0); });
    internal.forEach(c => {
      adjacency.get(c.fromId)?.push(c.toId);
      inDegree.set(c.toId, (inDegree.get(c.toId) || 0) + 1);
    });
    const queue = groupBlocks.filter(b => (inDegree.get(b.id) || 0) === 0).map(b => b.id);
    const result: string[] = [];
    while (queue.length > 0) {
      const id = queue.shift()!;
      result.push(id);
      for (const next of adjacency.get(id) || []) {
        inDegree.set(next, (inDegree.get(next) || 0) - 1);
        if (inDegree.get(next) === 0) queue.push(next);
      }
    }
    groupBlocks.forEach(b => { if (!result.includes(b.id)) result.push(b.id); });
    return result.map(id => groupBlocks.find(b => b.id === id)!).filter(Boolean);
  }

  function renderTreeGroup(group: BlockGroup, depth: number): React.ReactNode {
    const isExpanded = expandedNodes.has(group.id);
    const children = childGroupsOf(group.id);
    const groupBlocks = getGroupBlocks(group);

    return (
      <div key={group.id}>
        <button
          onClick={() => toggleNode(group.id)}
          className="flex items-center gap-2 w-full text-left py-1.5 px-2 rounded-md hover:bg-accent/50 transition-colors"
          style={{ paddingLeft: depth * 20 + 8 }}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          )}
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: group.color }} />
          <span className="text-sm font-semibold" style={{ color: group.color }}>
            {group.name || 'Group'}
          </span>
          <span className="text-xs text-muted-foreground">
            ({groupBlocks.length} blocks)
          </span>
        </button>

        {isExpanded && (
          <div className="border-l-2 ml-[11px]" style={{ borderColor: `${group.color}40`, paddingLeft: 0, marginLeft: depth * 20 + 18 }}>
            {groupBlocks.map(block => (
              <div
                key={block.id}
                className="flex items-center gap-2 py-1 px-3 text-xs"
              >
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: block.color }} />
                <span className="font-medium" style={{ color: block.color }}>
                  {block.config.name}
                </span>
                {block.config.description && (
                  <span className="text-muted-foreground truncate max-w-[300px]">
                    — {block.config.description.split('\n')[0]}
                  </span>
                )}
              </div>
            ))}
            {children.map(child => renderTreeGroup(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          onClick={expandAll}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-accent/50"
        >
          <ChevronsUpDown className="w-3.5 h-3.5" />
          Expand all
        </button>
        <button
          onClick={collapseAll}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-accent/50"
        >
          <ChevronsDownUp className="w-3.5 h-3.5" />
          Collapse all
        </button>
      </div>

      <div className="rounded-xl border p-2">
        {topLevelGroups.map(g => renderTreeGroup(g, 0))}

        {ungroupedBlocks.length > 0 && (
          <div className="mt-2 pt-2 border-t">
            <div className="flex items-center gap-2 py-1.5 px-2 text-sm font-semibold text-muted-foreground">
              Root (ungrouped)
              <span className="text-xs font-normal">({ungroupedBlocks.length} blocks)</span>
            </div>
            {ungroupedBlocks.map(block => (
              <div key={block.id} className="flex items-center gap-2 py-1 px-3 ml-5 text-xs">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: block.color }} />
                <span className="font-medium" style={{ color: block.color }}>
                  {block.config.name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <CrossGroupConnectors connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
    </div>
  );
}

// ─── COMPACT GRID VIEW ──────────────────────────────────────────────

